import { useDispatch } from "react-redux";
import fetchServer from "../controllers/fetchServer";
import {
  addInvoice,
  addInvoiceCustomers,
  addInvoiceServices,
  deleteInvoiceCustomer,
  deleteInvoiceService,
  setCustomers,
  setCustomersMeta,
  setInvoiceMeta,
  setInvoices,
  setServices,
  setServicesMeta,
  updateInvoice,
  updateInvoiceCustomer,
  updateInvoiceService,
} from "../redux/reducers/invoiceSlice";
import { enqueueSnackbar } from "notistack";
import moment from "moment";
import { useRef } from "react";
import axios from "axios";
import { URLdecode } from "../utils/url";

export default function useInvoiceApi() {
  const dispatch = useDispatch();
  const cancelValidateInvoiceToken = useRef(null);
  const cancelGetServicesToken = useRef(null);
  const cancelGetCustomersToken = useRef(null);
  const cancelGetInvoicesToken = useRef(null);

  const createService = async ({ data, setLoading, callback }) => {
    const payload = {
      title: data?.title,
      defaultUnitPrice: Number(data?.price),
      currency: data?.currency,
      description: [data.description],
      quantity: 1,
    };
    setLoading?.(true);
    await fetchServer({
      method: "POST",
      url: `/payment/v1/agentService`,
      data: payload,
    })
      .then((res) => {
        if (!res?.cancelled) setLoading && setLoading(false);
        if (res) {
          if (res.status === 201) {
            enqueueSnackbar("Service created successfully", {
              variant: "success",
            });
            const services = res?.data?.data;
            dispatch(addInvoiceServices([services]));
            callback?.(services);
          }
        }
      })
      .catch((err) => {});
  };

  const updateService = async ({ data, setLoading, callback, id }) => {
    const payload = {
      title: data?.title,
      defaultUnitPrice: Number(data?.price),
      currency: data?.currency,
      description: [data.description],
      quantity: 1,
    };
    setLoading?.(true);

    await fetchServer({
      method: "PATCH",
      url: `/payment/v1/agentService/${id}`,
      data: payload,
    })
      .then((res) => {
        if (!res?.cancelled) setLoading && setLoading(false);
        if (res) {
          if (res.status === 200) {
            enqueueSnackbar("Service updated successfully", {
              variant: "success",
            });
            const service = res?.data?.data;
            dispatch(updateInvoiceService(service));
            callback?.(service);
          } else if (res?.data?.code === 2005) {
            enqueueSnackbar(
              "Service is already used in an invoice and can not be edited",
              {
                variant: "warning",
              }
            );
          }
        }
      })
      .catch((err) => {});
  };

  const getServices = async ({ setLoading, callback, search }) => {
    const params = URLdecode();
    const cancellationMessage =
      "Invoice search request canceled due to new request.";

    if (cancelGetServicesToken.current) {
      cancelGetServicesToken.current.cancel(cancellationMessage);
    }

    cancelGetServicesToken.current = axios.CancelToken.source();
    setLoading?.(true);
    let queryParameters = {
      searchBy: "title",
      keyword: search,
    };

    if (params?.page) {
      let limit = Number(params?.limit || 10);
      queryParameters = {
        ...queryParameters,
        limit,
        offset: (Number(params?.page || 1) - 1) * limit,
      };
    }

    const queryParams = new URLSearchParams();

    Object.entries(queryParameters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => queryParams.append(key, v));
      } else if (value !== undefined && value !== "") {
        queryParams.append(key, value);
      }
    });
    await fetchServer({
      method: "GET",
      url: `/payment/v1/agentService?${queryParams.toString()}`,
      cancelToken: cancelGetServicesToken.current.token,
    })
      .then((res) => {
        if (!res?.cancelled) setLoading && setLoading(false);
        if (res) {
          if (res.status === 200) {
            const services = res?.data?.data;
            dispatch(setServices(services?.data));
            dispatch(setServicesMeta(services?.meta));
            callback?.();
          }
        }
      })
      .catch((err) => {});
  };

  const deleteService = async ({ setLoading, callback, id }) => {
    setLoading?.(true);
    await fetchServer({
      method: "DELETE",
      url: `/payment/v1/agentService/${id}`,
    })
      .then((res) => {
        if (!res?.cancelled) setLoading && setLoading(false);
        if (res) {
          if (res.status === 200) {
            enqueueSnackbar("Service deleted successfully", {
              variant: "success",
            });
            const service = res?.data?.data;
            dispatch(deleteInvoiceService(service));
            callback?.(service);
          } else if (res?.data?.code === 2005) {
            enqueueSnackbar(
              "Service is already used in an invoice and can not be deleted",
              {
                variant: "warning",
              }
            );
          }
        }
      })
      .catch((err) => {});
  };

  const generateInvoiceNo = async ({ setLoading, callback }) => {
    setLoading?.(true);
    await fetchServer({
      method: "GET",
      url: `/payment/v1/agentInvoice/generateInvoiceNo`,
    })
      .then((res) => {
        if (!res?.cancelled) setLoading && setLoading(false);
        if (res) {
          if (res.status === 200) {
            callback?.(res?.data?.data);
          }
        }
      })
      .catch((err) => {});
  };

  const validateInvoiceNo = async ({ setLoading, invoiceNo, callback }) => {
    const cancellationMessage = "Validation canceled due to new request.";

    if (cancelValidateInvoiceToken.current) {
      cancelValidateInvoiceToken.current.cancel(cancellationMessage);
    }

    cancelValidateInvoiceToken.current = axios.CancelToken.source();
    setLoading?.(true);
    await fetchServer({
      method: "POST",
      url: `/payment/v1/agentInvoice/validateInvoiceNo`,
      data: { invoiceNo },
      cancelToken: cancelValidateInvoiceToken.current.token,
    })
      .then((res) => {
        if (!res?.cancelled) setLoading && setLoading(false);
        if (res?.data && !res?.cancelled) {
          if (res.status === 200) {
            callback?.(true);
          } else {
            callback?.(false);
          }
        }
      })
      .catch((err) => {});
  };

  const createInvoice = async ({ data, setLoading, callback }) => {
    let payload = {
      invoiceNo: data?.invoiceNo,
      customer: data?.customer,
      notes: [data?.notes],
      paymentDue: moment(data?.date).format("YYYY-MM-DD"),
      services: data?.services,
      flightBookings:
        data?.flightBookings?.map((booking) => booking?.flightObj?._id) || [],
      hotelBookings: data?.hotelBookings?.map((booking) => booking?._id) || [],
      packageBookings:
        data?.packageBookings?.map((booking) => booking?._id) || [],
      tax: {
        value: Number(data?.tax?.value) || 0,
        taxMethod: data?.tax?.taxMethod,
      },
      serviceFee: {
        value: Number(data?.serviceFee?.value) || 0,
        feeMethod: data?.serviceFee?.feeMethod,
      },
    };
    if (data?.pdf) payload.pdf = data.pdf;
    if (data?.sendNow) payload.sendNow = data.sendNow;

    setLoading?.(true);
    await fetchServer({
      method: "POST",
      url: `/payment/v1/agentInvoice`,
      data: payload,
    })
      .then(async (res) => {
        if (!res?.cancelled) setLoading && setLoading(false);
        if (res) {
          if (res.status === 201) {
            enqueueSnackbar("Invoice created successfully", {
              variant: "success",
            });
            const invoice = res?.data?.data;
            dispatch(addInvoice([invoice]));
            await callback?.(invoice);
          }
        }
      })
      .catch((err) => {});
  };

  const createCustomer = async ({ data, setLoading, callback }) => {
    const payload = {
      firstName: data?.firstName,
      middleName: data?.middleName,
      lastName: data?.lastName,
      email: data?.email,
      address: data?.address,
      phone: data?.phoneNumber,
    };
    setLoading?.(true);
    await fetchServer({
      method: "POST",
      url: `/main/v1/customerAddressBook`,
      data: payload,
    })
      .then((res) => {
        if (!res?.cancelled) setLoading && setLoading(false);
        if (res) {
          if (res.status === 201) {
            enqueueSnackbar("Customer created successfully", {
              variant: "success",
            });
            const customer = res?.data?.data;
            dispatch(addInvoiceCustomers(customer));
            callback?.(customer);
          }
        }
      })
      .catch((err) => {});
  };

  const getCustomers = async ({ setLoading, callback, search, noSave }) => {
    const params = URLdecode();
    const cancellationMessage =
      "Customer search request canceled due to new request.";

    if (cancelGetCustomersToken.current) {
      cancelGetCustomersToken.current.cancel(cancellationMessage);
    }

    cancelGetCustomersToken.current = axios.CancelToken.source();
    setLoading?.(true);
    let queryParameters = {
      searchBy: "firstName,middleName,lastName",
      keyword: search,
    };

    if (params?.page && !noSave) {
      let limit = Number(params?.limit || 10);
      queryParameters = {
        ...queryParameters,
        limit,
        offset: (Number(params?.page || 1) - 1) * limit,
      };
    }

    const queryParams = new URLSearchParams();

    Object.entries(queryParameters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => queryParams.append(key, v));
      } else if (value !== undefined && value !== "") {
        queryParams.append(key, value);
      }
    });
    await fetchServer({
      method: "GET",
      url: `/main/v1/customerAddressBook?${queryParams.toString()}`,
      cancelToken: cancelGetCustomersToken.current.token,
    })
      .then((res) => {
        if (!res?.cancelled) setLoading && setLoading(false);
        if (res) {
          if (res.status === 200) {
            const customers = res?.data?.data;
            if (!noSave) {
              dispatch(setCustomers(customers?.data));
              dispatch(setCustomersMeta(customers?.meta));
            }
            callback?.(customers?.data);
          }
        }
      })
      .catch((err) => {});
  };

  const updateCustomer = async ({ data, setLoading, callback, id }) => {
    const payload = {
      firstName: data?.firstName,
      middleName: data?.middleName,
      lastName: data?.lastName,
      email: data?.email,
      address: data?.address,
      phone: data?.phoneNumber,
    };
    setLoading?.(true);
    await fetchServer({
      method: "PATCH",
      url: `/main/v1/customerAddressBook/${id}`,
      data: payload,
    })
      .then((res) => {
        if (!res?.cancelled) setLoading && setLoading(false);
        if (res) {
          if (res.status === 200) {
            enqueueSnackbar("Customer updated successfully", {
              variant: "success",
            });
            const customer = res?.data?.data;
            dispatch(updateInvoiceCustomer(customer));
            callback?.(customer);
          }
        }
      })
      .catch((err) => {});
  };

  const deleteCustomer = async ({ setLoading, callback, id }) => {
    setLoading?.(true);
    await fetchServer({
      method: "DELETE",
      url: `/main/v1/customerAddressBook/${id}`,
    })
      .then((res) => {
        if (!res?.cancelled) setLoading && setLoading(false);
        if (res) {
          if (res.status === 200) {
            enqueueSnackbar("Customer deleted successfully", {
              variant: "success",
            });
            const customer = res?.data?.data;
            dispatch(deleteInvoiceCustomer(customer));
            callback?.(customer);
          }
        }
      })
      .catch((err) => {});
  };

  const getInvoices = async ({
    setLoading,
    callback,
    search,
    noSave,
    limit,
    offset,
  }) => {
    const params = URLdecode();
    const cancellationMessage =
      "Invoice search request canceled due to new request.";

    if (cancelGetInvoicesToken.current) {
      cancelGetInvoicesToken.current.cancel(cancellationMessage);
    }

    cancelGetInvoicesToken.current = axios.CancelToken.source();
    setLoading?.(true);
    let queryParameters = {
      searchBy:
        "customer.firstName,customer.middleName,customer.lastName,invoiceNo",
      keyword: search,
      "populate[]": [
        "customer",
        "services.service",
        "packageBookings",
        "hotelBookings",
        "flightBookings",
      ],
    };
    if (params?.filter && params?.filter !== "All") {
      queryParameters = {
        ...queryParameters,
        filterBy: "status",
        filterValue: params?.filter,
      };
    }
    if (params?.page) {
      let limit = Number(params?.limit || 10);
      queryParameters = {
        ...queryParameters,
        limit,
        offset: (Number(params?.page || 1) - 1) * limit,
      };
    }
    if (limit) queryParameters.limit = limit;
    if (offset) queryParameters.offset = offset;

    const queryParams = new URLSearchParams();

    Object.entries(queryParameters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => queryParams.append(key, v));
      } else if (value !== undefined && value !== "") {
        queryParams.append(key, value);
      }
    });
    await fetchServer({
      method: "GET",
      url: `/payment/v1/agentInvoice?${queryParams.toString()}`,
      cancelToken: cancelGetInvoicesToken.current.token,
    })
      .then((res) => {
        if (!res?.cancelled) setLoading && setLoading(false);
        if (res) {
          if (res.status === 200) {
            const invoices = res?.data?.data;
            if (!noSave) {
              dispatch(setInvoices(invoices?.data));
              dispatch(setInvoiceMeta(invoices?.meta));
            }
            callback?.(res?.data);
          }
        }
      })
      .catch((err) => {});
  };

  const markInvoiceAsPaid = async ({ setLoading, callback, id, invoice }) => {
    setLoading?.(true);
    await fetchServer({
      method: "PATCH",
      url: `/payment/v1/agentInvoice/${id}/paid`,
    })
      .then((res) => {
        if (!res?.cancelled) setLoading && setLoading(false);
        if (res) {
          if (res.status === 200) {
            enqueueSnackbar("Invoice marked as paid successfully", {
              variant: "success",
            });
            const updatedInvoice = res?.data?.data;
            dispatch(
              updateInvoice({ ...invoice, status: updatedInvoice?.status })
            );
            callback?.(invoice);
          }
        }
      })
      .catch((err) => {});
  };

  const voidInvoice = async ({ setLoading, callback, id, invoice }) => {
    setLoading?.(true);
    await fetchServer({
      method: "PATCH",
      url: `/payment/v1/agentInvoice/${id}/void`,
    })
      .then((res) => {
        if (!res?.cancelled) setLoading && setLoading(false);
        if (res) {
          if (res.status === 200) {
            enqueueSnackbar("Invoice voided successfully", {
              variant: "success",
            });
            const updatedInvoice = res?.data?.data;
            dispatch(
              updateInvoice({ ...invoice, status: updatedInvoice?.status })
            );
            callback?.(invoice);
          }
        }
      })
      .catch((err) => {});
  };

  const sendInvoice = async ({ setLoading, callback, id, invoice }) => {
    setLoading?.(true);
    await fetchServer({
      method: "POST",
      url: `/payment/v1/agentInvoice/sendInvoice/${id}`,
    })
      .then((res) => {
        if (!res?.cancelled) setLoading && setLoading(false);
        if (res) {
          if (res.status === 200) {
            enqueueSnackbar("Invoice sent successfully", {
              variant: "success",
            });
            // const updatedInvoice = res?.data?.data;
            // dispatch(
            //   updateInvoice({ ...invoice, status: updatedInvoice?.status })
            // );
            callback?.(invoice);
          }
        }
      })
      .catch((err) => {});
  };

  return {
    createService,
    updateService,
    getServices,
    deleteService,
    generateInvoiceNo,
    createInvoice,
    validateInvoiceNo,
    createCustomer,
    getCustomers,
    updateCustomer,
    deleteCustomer,
    getInvoices,
    markInvoiceAsPaid,
    voidInvoice,
    sendInvoice,
  };
}
